import { useMemo } from 'react';

import { evaluateComponentReadiness } from '@/shared/components/modals/ResourceActionModals/EditReportComponentModal/helpers/evaluateComponentReadiness';
import {
    useReportData,
    useReportQuery,
} from '@/shared/components/modals/ResourceActionModals/EditReportComponentModal/helpers/hook';
import { chartTransformers } from '@/shared/components/modals/ResourceActionModals/EditReportComponentModal/helpers/transformers';
import { ReportComponent, ReportComponentType } from '@/types';

const hasRenderableData = (data: unknown) => {
    if (Array.isArray(data)) {
        return data.length > 0;
    }

    if (!data || typeof data !== 'object') {
        return false;
    }

    const record = data as Record<string, unknown>;

    if (Array.isArray(record.nodes) && Array.isArray(record.links)) {
        return record.nodes.length > 0 && record.links.length > 0;
    }

    if (Array.isArray(record.data)) {
        return record.data.length > 0;
    }

    return Object.keys(record).length > 0;
};

const createEmptyChartData = <T>(fallbackData: T): T => {
    if (Array.isArray(fallbackData)) {
        return [] as T;
    }

    if (!fallbackData || typeof fallbackData !== 'object') {
        return fallbackData;
    }

    const fallbackRecord = fallbackData as Record<string, unknown>;

    if (Array.isArray(fallbackRecord.nodes) && Array.isArray(fallbackRecord.links)) {
        return { ...fallbackRecord, nodes: [], links: [] } as T;
    }

    if (Array.isArray(fallbackRecord.keys) && Array.isArray(fallbackRecord.data)) {
        return { ...fallbackRecord, keys: [], data: [] } as T;
    }

    if (Array.isArray(fallbackRecord.data)) {
        return { ...fallbackRecord, data: [] } as T;
    }

    return {} as T;
};

export const useNivoReportData = <T>(component: ReportComponent, fallbackData: T): T => {
    const query = useReportQuery(component);
    const { data: rawData } = useReportData(component);
    const transformer = chartTransformers[component.type as ReportComponentType];
    const { isReady } = evaluateComponentReadiness(component);

    return useMemo(() => {
        if (!isReady) {
            return fallbackData;
        }

        if (!transformer) {
            return createEmptyChartData(fallbackData);
        }

        const transformed = transformer(rawData, query);

        return hasRenderableData(transformed) ? (transformed as T) : createEmptyChartData(fallbackData);
    }, [fallbackData, isReady, query, rawData, transformer]);
};
